class HttpError extends Error {
  constructor(response, data, requestConfig = {}) {
    super(`Request failed with status ${response.status}`)
    this.name = 'HttpError'
    this.response = {
      status: response.status,
      data: data,
      headers: response.headers
    }
    this.request = {
      responseType: requestConfig.responseType || ''
    }
  }
}

const serializeParams = params => {
  if (!params || typeof params !== 'object') return ''

  const searchParams = new URLSearchParams()

  const flatten = (obj, prefix = '') => {
    for (const [key, value] of Object.entries(obj)) {
      const fullKey = prefix ? `${prefix}[${key}]` : key

      if (value === null || value === undefined) continue

      if (Array.isArray(value)) {
        value.forEach(item => {
          searchParams.append(`${fullKey}[]`, item)
        })
      } else if (typeof value === 'object') {
        flatten(value, fullKey)
      } else {
        searchParams.append(fullKey, value)
      }
    }
  }

  flatten(params)
  const result = searchParams.toString()
  return result ? `?${result}` : ''
}

const executeWithRetry = async (fn, retryConfig) => {
  const { retries = 0, retryDelay = () => 1000 } = retryConfig

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn()
    } catch (err) {
      if (attempt === retries) throw err
      if (err.response && err.response.status < 500) throw err
      await new Promise(resolve => setTimeout(resolve, retryDelay(attempt + 1)))
    }
  }
}

const parseResponse = async (response, config) => {
  const contentType = response.headers.get('content-type') || ''

  if (config.responseType === 'arraybuffer') {
    return response.arrayBuffer()
  }

  if (config.responseType === 'blob') {
    return response.blob()
  }

  if (contentType.includes('json')) {
    const text = await response.text()
    return text ? JSON.parse(text) : null
  }

  return response.text()
}

const createXHRRequest = (method, url, data, config) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const {
      params,
      signal,
      responseType,
      returnFullResponse,
      onUploadProgress,
      onDownloadProgress,
      requestInterceptor,
      errorInterceptor,
      ...restConfig
    } = config

    let fullURL = `${(config.baseURL || '').replace(/\/$/, '')}/${url.replace(/^\//, '')}`
    if (params) {
      fullURL += serializeParams(params)
    }

    xhr.open(method, fullURL)

    xhr.setRequestHeader('Accept', 'application/json')

    if (!(data instanceof FormData)) {
      xhr.setRequestHeader('Content-Type', 'application/json')
    }

    if (requestInterceptor) {
      const extraHeaders = requestInterceptor()
      if (extraHeaders) {
        Object.entries(extraHeaders).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value)
        })
      }
    }

    if (onUploadProgress || onDownloadProgress) {
      xhr.upload.onprogress = event => {
        if (event.lengthComputable && onUploadProgress) {
          onUploadProgress({
            loaded: event.loaded,
            total: event.total,
            percentage: Math.round((event.loaded * 100) / event.total)
          })
        }
      }
    }

    if (onDownloadProgress) {
      xhr.onprogress = event => {
        if (event.lengthComputable) {
          onDownloadProgress({
            loaded: event.loaded,
            total: event.total,
            percentage: Math.round((event.loaded * 100) / event.total)
          })
        }
      }
    }

    const handleXHRError = error => {
      if (errorInterceptor) {
        try {
          const result = errorInterceptor(error, () => createXHRRequest(method, url, data, config))
          resolve(result)
          return
        } catch (interceptedError) {
          reject(interceptedError)
          return
        }
      }
      reject(error)
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (responseType === 'arraybuffer') {
          const response = xhr.response
          if (returnFullResponse) {
            resolve({ data: response, status: xhr.status, headers: parseHeaders(xhr.getAllResponseHeaders()) })
          } else {
            resolve(response)
          }
          return
        }
        if (responseType === 'blob') {
          const response = xhr.response
          if (returnFullResponse) {
            resolve({ data: response, status: xhr.status, headers: parseHeaders(xhr.getAllResponseHeaders()) })
          } else {
            resolve(response)
          }
          return
        }

        let parsedData = xhr.responseText
        const contentType = xhr.getResponseHeader('content-type') || ''
        if (contentType.includes('json') && parsedData) {
          parsedData = JSON.parse(parsedData)
        }

        if (returnFullResponse) {
          resolve({ data: parsedData, status: xhr.status, headers: parseHeaders(xhr.getAllResponseHeaders()) })
        } else {
          resolve(parsedData)
        }
      } else {
        let errorData = xhr.responseText
        const contentType = xhr.getResponseHeader('content-type') || ''
        if (contentType.includes('json') && errorData) {
          errorData = JSON.parse(errorData)
        }
        const error = new HttpError({ status: xhr.status }, errorData, { responseType })
        handleXHRError(error)
      }
    }

    xhr.onerror = () => {
      const networkError = new Error('Network error')
      networkError.name = 'NetworkError'
      networkError.response = undefined
      networkError.request = { responseType: responseType || '' }
      handleXHRError(networkError)
    }

    xhr.ontimeout = () => {
      const timeoutError = new Error('Request timeout')
      timeoutError.name = 'TimeoutError'
      handleXHRError(timeoutError)
    }

    if (signal) {
      signal.addEventListener('abort', () => {
        xhr.abort()
      })
    }

    if (data instanceof FormData) {
      xhr.send(data)
    } else if (data !== undefined && data !== null) {
      xhr.send(JSON.stringify(data))
    } else {
      xhr.send()
    }
  })
}

const parseHeaders = rawHeaders => {
  const headers = new Headers()
  rawHeaders.split('\r\n').forEach(line => {
    const parts = line.split(': ')
    if (parts.length === 2) {
      headers.append(parts[0].toLowerCase(), parts[1])
    }
  })
  return headers
}

const parseErrorResponse = async response => {
  const contentType = response.headers.get('content-type') || ''

  if (contentType.includes('json')) {
    const text = await response.text()
    return text ? JSON.parse(text) : null
  }

  return response.text()
}

export function createHttpClient({ baseURL, requestInterceptor, errorInterceptor }) {
  const request = async (method, url, data, config = {}) => {
    const {
      params,
      signal,
      responseType,
      returnFullResponse,
      retry: retryConfig,
      onUploadProgress,
      onDownloadProgress,
      ...restConfig
    } = config

    const useXHR = onUploadProgress || onDownloadProgress

    if (useXHR) {
      const xhrConfig = {
        ...config,
        baseURL,
        requestInterceptor,
        errorInterceptor,
        onUploadProgress: onUploadProgress
          ? event => {
              if (event.lengthComputable) {
                onUploadProgress({
                  loaded: event.loaded,
                  total: event.total,
                  percentage: Math.round((event.loaded * 100) / event.total)
                })
              }
            }
          : undefined,
        onDownloadProgress
      }
      const doXHR = () => createXHRRequest(method, url, data, xhrConfig)

      if (retryConfig) {
        return executeWithRetry(doXHR, retryConfig)
      }

      return doXHR()
    }

    const fullURL = `${baseURL.replace(/\/$/, '')}/${url.replace(/^\//, '')}${serializeParams(params)}`

    const isFormData = data instanceof FormData

    // Rebuild headers on every attempt so retries pick up the freshest Authorization token (mirrors XHR path).
    const doFetch = async () => {
      const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }

      if (requestInterceptor) {
        const extraHeaders = requestInterceptor()
        if (extraHeaders) {
          Object.assign(headers, extraHeaders)
        }
      }

      if (isFormData) {
        delete headers['Content-Type']
      }

      const fetchOptions = {
        method,
        headers,
        signal
      }

      if (data !== undefined && data !== null) {
        fetchOptions.body = isFormData ? data : JSON.stringify(data)
      }

      let response
      try {
        response = await fetch(fullURL, fetchOptions)
      } catch (err) {
        if (err.name === 'AbortError') throw err

        const networkError = new Error(err.message)
        networkError.name = 'NetworkError'
        networkError.response = undefined
        networkError.request = { responseType: responseType || '' }

        if (errorInterceptor) {
          return errorInterceptor(networkError, doFetch)
        }
        throw networkError
      }

      if (!response.ok) {
        const errorData = await parseErrorResponse(response)
        const error = new HttpError(response, errorData, { responseType })

        if (errorInterceptor) {
          return errorInterceptor(error, doFetch)
        }
        throw error
      }

      const parsedData = await parseResponse(response, { responseType })

      if (returnFullResponse) {
        return {
          data: parsedData,
          status: response.status,
          headers: response.headers
        }
      }

      return parsedData
    }

    if (retryConfig) {
      return executeWithRetry(doFetch, retryConfig)
    }

    return doFetch()
  }

  const client = {
    get: (url, config) => request('GET', url, undefined, config),
    post: (url, data, config) => request('POST', url, data, config),
    put: (url, data, config) => request('PUT', url, data, config),
    patch: (url, data, config) => request('PATCH', url, data, config),
    delete: (url, config = {}) => {
      const { data, ...rest } = config
      return request('DELETE', url, data, rest)
    },
    postForm: (url, data, config) => request('POST', url, data, config),
    putForm: (url, data, config) => request('PUT', url, data, config),
    patchForm: (url, data, config) => request('PATCH', url, data, config)
  }

  return client
}

export { HttpError }
